import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { fmtDate, fmtTime } from "@/components/SessionCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { LogOut, ShieldAlert, Check, X, NotebookPen, Dumbbell } from "lucide-react";

export function CoachDashboard() {
  const { t, tl, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const hasMine = sessions.some((s) => s.is_mine);
  const visible = onlyMine ? sessions.filter((s) => s.is_mine) : sessions;
  const [active, setActive] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState({ objective: "", exercises: "", observations: "", next_objective: "" });

  useEffect(() => { api.get("/coach/sessions").then(({ data }) => setSessions(data)).catch(() => {}); }, []);

  const openSession = async (s) => {
    setActive(s);
    const { data } = await api.get(`/coach/sessions/${s.session_id}/participants`);
    setParticipants(data);
  };

  const mark = async (rid, status) => {
    try { await api.post("/coach/attendance", { reservation_id: rid, status }); toast.success(t("common.success")); openSession(active); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveNote = async () => {
    try {
      await api.post("/coach/notes", { client_id: noteFor.client.user_id, session_id: active?.session_id, ...note });
      toast.success(t("common.success")); setNoteFor(null); setNote({ objective: "", exercises: "", observations: "", next_objective: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-40 flex items-center justify-between border-b border-border/60 px-5 py-3.5">
        <span className="font-display text-xl font-bold">Frenchies · {t("nav.coach")}</span>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && <Button size="sm" variant="outline" onClick={() => navigate("/admin")} className="rounded-full">Admin</Button>}
          <LanguageSwitcher />
          <button data-testid="coach-logout" onClick={logout} className="rounded-full p-2 hover:bg-accent"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("schedule.title")}</h1>
          {!active && hasMine && (
            <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm" data-testid="coach-filter">
              <button data-testid="coach-filter-all" onClick={() => setOnlyMine(false)} className={`rounded-full px-3 py-1 ${!onlyMine ? "bg-primary text-primary-foreground" : ""}`}>{tl({ es: "Todas las clases", en: "All classes", fr: "Tous les cours" })}</button>
              <button data-testid="coach-filter-mine" onClick={() => setOnlyMine(true)} className={`rounded-full px-3 py-1 ${onlyMine ? "bg-primary text-primary-foreground" : ""}`}>{tl({ es: "Mis clases", en: "My classes", fr: "Mes cours" })}</button>
            </div>
          )}
        </div>
        {!active ? (
          <div className="space-y-2">
            {visible.length === 0 && <p className="text-muted-foreground">{t("common.noData")}</p>}
            {visible.map((s) => (
              <button key={s.session_id} data-testid={`coach-session-${s.session_id}`} onClick={() => openSession(s)}
                className={`flex w-full items-center justify-between rounded-xl border bg-card px-4 py-3 text-left hover:bg-accent ${s.is_mine ? "border-primary/50" : "border-border"}`}>
                <div>
                  <div className="font-medium">{tl(s.class_type?.name)}{s.is_mine && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{tl({ es: "Mía", en: "Mine", fr: "Moi" })}</span>}</div>
                  <div className="text-sm text-muted-foreground">{fmtDate(s.start, lang)} {fmtTime(s.start, lang)} · {s.booked_count}/{s.capacity}{s.coach?.name ? ` · ${s.coach.name}` : ""}</div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <Button variant="ghost" onClick={() => setActive(null)} className="mb-4 rounded-full">← {t("common.back")}</Button>
            <h2 className="mb-4 font-display text-xl font-semibold">{tl(active.class_type?.name)} · {fmtTime(active.start, lang)}</h2>
            <div className="space-y-2">
              {participants.length === 0 && <p className="text-muted-foreground">{t("common.noData")}</p>}
              {participants.map((p) => (
                <div key={p.reservation_id} data-testid={`participant-${p.reservation_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 font-medium">{p.client?.name}
                      {p.client?.health_flag === "attention" && <ShieldAlert className="h-4 w-4 text-destructive" />}
                      {p.client?.absence?.active && (
                        <span data-testid={`sanction-${p.reservation_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-500">
                          <Dumbbell className="h-3 w-3" />{p.client.absence.burpees} burpees
                        </span>
                      )}
                    </div>
                    {p.client?.injuries && <div className="text-xs text-muted-foreground">⚠ {p.client.injuries}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium">
                      <input type="checkbox" data-testid={`present-${p.reservation_id}`} checked={p.status === "attended"} onChange={() => mark(p.reservation_id, "attended")} className="h-4 w-4 accent-emerald-500" />
                      {tl({ es: "Presente", en: "Present", fr: "Présent" })}
                    </label>
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium">
                      <input type="checkbox" data-testid={`absent-${p.reservation_id}`} checked={p.status === "no_show"} onChange={() => mark(p.reservation_id, "no_show")} className="h-4 w-4 accent-red-500" />
                      {tl({ es: "Ausente", en: "Absent", fr: "Absent" })}
                    </label>
                    <button data-testid={`note-${p.reservation_id}`} onClick={() => setNoteFor(p)} className="rounded-full bg-secondary p-2"><NotebookPen className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Coach note · {noteFor?.client?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="note-objective" placeholder="Objetivo del día" value={note.objective} onChange={(e) => setNote({ ...note, objective: e.target.value })} />
            <Textarea data-testid="note-exercises" placeholder="Ejercicios realizados" value={note.exercises} onChange={(e) => setNote({ ...note, exercises: e.target.value })} />
            <Textarea data-testid="note-observations" placeholder="Observaciones" value={note.observations} onChange={(e) => setNote({ ...note, observations: e.target.value })} />
            <Textarea data-testid="note-next" placeholder="Objetivo próxima sesión" value={note.next_objective} onChange={(e) => setNote({ ...note, next_objective: e.target.value })} />
          </div>
          <DialogFooter><Button data-testid="save-note-btn" onClick={saveNote} className="rounded-full">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
